import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** The pages to show: the first, the last, and the current one with its neighbours; 0 marks a gap. */
export function pageWindow(page: number, pages: number): number[] {
  const keep = new Set([1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages));
  const sorted = [...keep].sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1]! > 1 ? [0, p] : [p]));
}

export function Pagination({
  page,
  pages,
  onPageChange,
  label = "Pagination",
}: {
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
  label?: string;
}) {
  const step = "inline-flex h-11 items-center gap-1 rounded-pill px-3 text-sm text-secondary can-hover:hover:bg-surface-muted disabled:text-muted disabled:hover:bg-transparent";
  return (
    <nav data-slot="pagination" aria-label={label} className="flex flex-wrap items-center gap-1">
      <button type="button" className={step} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        <ChevronLeft aria-hidden className="size-4" />
        Previous
      </button>
      {pageWindow(page, pages).map((item, index) =>
        item === 0 ? (
          <span key={`gap-${index}`} aria-hidden className="px-1 text-muted">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-label={`Page ${item}`}
            aria-current={item === page ? "page" : undefined}
            className={cn("grid size-11 place-items-center rounded-pill text-sm", item === page ? "bg-surface-muted font-medium text-foreground" : "text-secondary can-hover:hover:bg-surface-muted")}
            onClick={() => onPageChange(item)}
          >
            {item}
          </button>
        ),
      )}
      <button type="button" className={step} disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
        Next
        <ChevronRight aria-hidden className="size-4" />
      </button>
    </nav>
  );
}
